export type ImageGenerationProvider = "gemini" | "openrouter";

export type AspectRatio = "1:1" | "3:2" | "2:3" | "16:9" | "4:3";

export const ASPECT_RATIOS: readonly AspectRatio[] = ["1:1", "3:2", "2:3", "16:9", "4:3"];

export const DEFAULT_ASPECT_RATIO: AspectRatio = "1:1";

export const IMAGE_GENERATION_MODELS = [
  { id: "gemini-3.1-flash-lite-image", label: "Gemini 3.1 Flash Lite Image", provider: "gemini", providerLabel: "Google", providerModel: "gemini-3.1-flash-lite-image", creditsPerKInputTokens: 55, creditsPerKOutputTokens: 332, selectable: true, supportsReferenceImage: true },
  { id: "gemini-2.5-flash-image", label: "Gemini 2.5 Flash Image", provider: "gemini", providerLabel: "Google", providerModel: "gemini-2.5-flash-image", creditsPerKInputTokens: 72, creditsPerKOutputTokens: 9, selectable: true, supportsReferenceImage: true },
  { id: "gemini-3.1-flash-image", label: "Gemini 3.1 Flash Image", provider: "gemini", providerLabel: "Google", providerModel: "gemini-3.1-flash-image", creditsPerKInputTokens: 47, creditsPerKOutputTokens: 5613, selectable: true, supportsReferenceImage: true },
  { id: "gemini-3-pro-image", label: "Gemini 3 Pro Image", provider: "gemini", providerLabel: "Google", providerModel: "gemini-3-pro-image", creditsPerKInputTokens: 168, creditsPerKOutputTokens: 10080, selectable: true, supportsReferenceImage: true },
  { id: "openrouter-grok-imagine-image-quality", label: "Grok Imagine Image Quality", provider: "openrouter", providerLabel: "OpenRouter", providerModel: "x-ai/grok-imagine-image-quality", creditsPerKInputTokens: 2, creditsPerKOutputTokens: 12, selectable: true, supportsReferenceImage: true },
  { id: "openrouter-seedream-4.5", label: "Seedream 4.5", provider: "openrouter", providerLabel: "OpenRouter", providerModel: "bytedance-seed/seedream-4.5", creditsPerKInputTokens: 0, creditsPerKOutputTokens: 10, selectable: true, supportsReferenceImage: false },
  { id: "openrouter-recraft-v4.1-pro", label: "Recraft V4.1 Pro", provider: "openrouter", providerLabel: "OpenRouter", providerModel: "recraft/recraft-v4.1-pro", creditsPerKInputTokens: 0, creditsPerKOutputTokens: 50, selectable: true, supportsReferenceImage: false },
  { id: "openrouter-flux.2-pro", label: "FLUX.2 Pro", provider: "openrouter", providerLabel: "OpenRouter", providerModel: "black-forest-labs/flux.2-pro", creditsPerKInputTokens: 0, creditsPerKOutputTokens: 7, selectable: true, supportsReferenceImage: false },
] as const satisfies readonly {
  id: string;
  label: string;
  provider: ImageGenerationProvider;
  providerLabel: string;
  providerModel: string;
  creditsPerKInputTokens: number;
  creditsPerKOutputTokens: number;
  selectable: boolean;
  /** Whether this model accepts a reference image alongside the prompt. */
  supportsReferenceImage: boolean;
}[];

export type ImageGenerationModel = (typeof IMAGE_GENERATION_MODELS)[number];
export type ImageGenerationModelId = ImageGenerationModel["id"];

export const DEFAULT_IMAGE_GENERATION_MODEL_ID: ImageGenerationModelId = "gemini-3.1-flash-image";

// Single source of truth for "this is the Grok model" — reused by both the reference-image
// support check and the server's provider derivation when saving a generated image, instead of
// hardcoding "openrouter-grok-imagine-image-quality" independently in each place.
export const GROK_IMAGE_MODEL_ID: ImageGenerationModelId = "openrouter-grok-imagine-image-quality";

export function isImageGenerationModelId(value: unknown): value is ImageGenerationModelId {
  return typeof value === "string" && IMAGE_GENERATION_MODELS.some((model) => model.id === value && model.selectable);
}

export function resolveImageGenerationModel(value?: unknown): ImageGenerationModel {
  const resolved = isImageGenerationModelId(value)
    ? IMAGE_GENERATION_MODELS.find((model) => model.id === value)
    : undefined;
  return resolved ?? IMAGE_GENERATION_MODELS.find((model) => model.id === DEFAULT_IMAGE_GENERATION_MODEL_ID)!;
}

// Recraft and FLUX ignore an aspect_ratio request and return their native resolution (see the
// comment in src/ai/openRouterImage.ts), so surfaces that depend on a non-square ratio should not
// offer them.
const MODEL_IDS_IGNORING_ASPECT_RATIO: ReadonlySet<string> = new Set<ImageGenerationModelId>([
  "openrouter-recraft-v4.1-pro",
  "openrouter-flux.2-pro",
]);

export function imageGenerationModelHonorsAspectRatio(value?: unknown): boolean {
  return !MODEL_IDS_IGNORING_ASPECT_RATIO.has(resolveImageGenerationModel(value).id);
}

export function calculateImageGenerationCredits(
  modelId: unknown,
  inputTokens: number,
  outputTokens: number,
): number {
  const model = resolveImageGenerationModel(modelId);
  const inputCost = inputTokens * (model.creditsPerKInputTokens / 1000);
  const outputCost = outputTokens * (model.creditsPerKOutputTokens / 1000);
  return Math.round(inputCost + outputCost);
}
